import React, { useEffect, useState, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  CircularProgress,
  Paper,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
  CardActions,
  Alert,
  Box,
  Divider,
  Grid2,
} from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import moment from "moment";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import CheckoutForm from "./CheckoutForm";
import { useAuth } from "../../utils/auth";

const appearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "#ff0000",
    fontFamily: "Roboto Mono, monospace",
  },
};

const Payment = () => {
  const { user } = useAuth();
  const {
    email,
    stripeCustomerId,
    coachAccountId,
    isCoach,
    loading: userInfoLoading,
  } = useUserInfo();
  const stripePromise = useMemo(() => {
    return loadStripe(
      import.meta.env.VITE_STRIPE_PUBLIC_KEY,
      coachAccountId ? { stripeAccount: coachAccountId } : undefined
    );
  }, [coachAccountId]);
  const [userProducts, setUserProducts] = useState([]);
  const [purchasedProducts, setPurchasedProducts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState();
  const { getUserProducts, getPaymentMethods } = useApi();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [disabledPriceIds, setDisabledPriceIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const send = async () => {
      const [assignedProducts, purchased, paymentMethodResponse] =
        await Promise.all([
          getUserProducts(user.uid, "assigned"),
          getUserProducts(user.uid, "purchased"),
          stripeCustomerId
            ? getPaymentMethods(stripeCustomerId, coachAccountId)
            : Promise.resolve({
                paymentMethods: [],
                defaultPaymentMethodId: undefined,
              }),
        ]);
      setUserProducts(assignedProducts);
      setPurchasedProducts(purchased);
      setPaymentMethods(paymentMethodResponse?.paymentMethods || []);
      setDefaultPaymentMethodId(
        paymentMethodResponse?.defaultPaymentMethodId || undefined
      );
      const purchasedActivePriceIds =
        (purchased || [])
          .filter(
            (p) =>
              (!p.recurring &&
                typeof p.quantityLeft === "number" &&
                p.quantityLeft > 0) ||
              (p.recurring && p.status === "active")
          )
          .map((p) => p.priceId) || [];
      if (purchasedActivePriceIds.length > 0) {
        setDisabledPriceIds((prev) =>
          Array.from(new Set([...prev, ...purchasedActivePriceIds]))
        );
      }
      setLoading(false);
    };
    if (user?.uid && !isCoach && !userInfoLoading) {
      send();
    }
  }, [getUserProducts, user, stripeCustomerId, isCoach, userInfoLoading]);

  if (isCoach && !userInfoLoading) {
    return <Navigate to="/clients" replace />;
  }

  const handleProductSelect = (product) => {
    if (disabledPriceIds.includes(product.priceId)) {
      return;
    }
    setSelectedProducts((prev) => {
      // If it's a recurring product
      if (product.recurring) {
        // If this product is already selected, remove it
        if (prev.some((p) => p.priceId === product.priceId)) {
          return prev.filter((p) => p.priceId !== product.priceId);
        }
        // Otherwise, remove any other recurring product and add this one
        return [...prev.filter((p) => !p.recurring), product];
      }
      // If it's a one-time product
      else {
        // If this product is already selected, remove it
        if (prev.some((p) => p.priceId === product.priceId)) {
          return prev.filter((p) => p.priceId !== product.priceId);
        }
        // Otherwise, add it to the list
        return [...prev, product];
      }
    });
  };

  const handlePartialSuccess = (successfulItems = []) => {
    const successPriceIds = successfulItems
      .map((item) => item.priceId)
      .filter(Boolean);
    if (!successPriceIds.length) {
      return;
    }
    setDisabledPriceIds((prev) =>
      Array.from(new Set([...prev, ...successPriceIds]))
    );
    setSelectedProducts((prev) =>
      prev.filter((product) => !successPriceIds.includes(product.priceId))
    );
  };

  const handlePaymentMethodAdded = (paymentMethod) => {
    if (!paymentMethod?.id) return;
    setPaymentMethods((prev) => {
      if (prev.some((pm) => pm.id === paymentMethod.id)) {
        return prev;
      }
      return [...prev, paymentMethod];
    });
    if (!defaultPaymentMethodId) {
      setDefaultPaymentMethodId(paymentMethod.id);
    }
  };

  // Separate products into recurring and one-time
  const recurringProducts = userProducts.filter((product) => product.recurring);
  const oneTimeProducts = userProducts.filter((product) => !product.recurring);

  if (loading) {
    return (
      <Grid2 display="flex" justifyContent="center" sx={{ mt: 2 }}>
        <CircularProgress />
      </Grid2>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Elements
        stripe={stripePromise}
        options={{
          appearance,
          fonts: [
            {
              family: "Roboto Mono, monospace",
              cssSrc:
                "https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@100;400;500;700",
            },
          ],
        }}
      >
        <>
          {loading ? (
            <Grid2 display="flex" justifyContent="center" sx={{ mt: 2 }}>
              <CircularProgress />
            </Grid2>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Select Products
              </Typography>

              {/* Recurring Products Section */}
              {recurringProducts && recurringProducts.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                    Recurring Subscriptions
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Select a recurring subscription.
                  </Typography>
                  <Grid2 container spacing={2}>
                    {recurringProducts.map((product) => {
                      const isDisabled = disabledPriceIds.includes(
                        product.priceId
                      );
                      const isSelected = selectedProducts.some(
                        (p) => p.priceId === product.priceId
                      );
                      return (
                        <Grid2
                          item
                          size={{ xs: 12, sm: 6, md: 4 }}
                          key={product.priceId}
                        >
                          <Card
                            onClick={() =>
                              !isDisabled && handleProductSelect(product)
                            }
                            sx={{
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              opacity: isDisabled ? 0.6 : 1,
                              border:
                                !isDisabled && isSelected
                                  ? "2px solid #ff0000"
                                  : "none",
                            }}
                          >
                            <CardContent>
                              <Typography variant="h6">
                                {product.productName}
                              </Typography>
                              <Typography variant="body2">
                                {product.priceDescription}
                              </Typography>
                              <Typography variant="h5">
                                ${product.price.toFixed(2)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Recurring Subscription
                              </Typography>
                            </CardContent>
                            <CardActions>
                              <Button size="small" disabled={isDisabled}>
                                {isDisabled
                                  ? "Purchased"
                                  : isSelected
                                  ? "Selected"
                                  : "Select"}
                              </Button>
                            </CardActions>
                          </Card>
                        </Grid2>
                      );
                    })}
                  </Grid2>
                </>
              )}
              <Divider sx={{ my: 4 }} />

              {/* One-Time Products Section */}
              {oneTimeProducts && oneTimeProducts.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    One-Time Purchases
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Select one or more one-time purchases.
                  </Typography>
                  <Grid2 container spacing={2}>
                    {oneTimeProducts.map((product) => {
                      const isDisabled = disabledPriceIds.includes(
                        product.priceId
                      );
                      const isSelected = selectedProducts.some(
                        (p) => p.priceId === product.priceId
                      );
                      return (
                        <Grid2
                          item
                          size={{ xs: 12, sm: 6, md: 4 }}
                          key={product.priceId}
                        >
                          <Card
                            onClick={() =>
                              !isDisabled && handleProductSelect(product)
                            }
                            sx={{
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              opacity: isDisabled ? 0.6 : 1,
                              border:
                                !isDisabled && isSelected
                                  ? "2px solid #ff0000"
                                  : "none",
                            }}
                          >
                            <CardContent>
                              <Typography variant="h6">
                                {product.productName}
                              </Typography>
                              <Typography variant="body2">
                                {product.priceDescription}
                              </Typography>
                              <Typography variant="h5">
                                ${product.price.toFixed(2)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                One-time Purchase
                              </Typography>
                            </CardContent>
                            <CardActions>
                              <Button size="small" disabled={isDisabled}>
                                {isDisabled
                                  ? "Purchased"
                                  : isSelected
                                  ? "Selected"
                                  : "Select"}
                              </Button>
                            </CardActions>
                          </Card>
                        </Grid2>
                      );
                    })}
                  </Grid2>
                </>
              )}

              {selectedProducts.length > 0 && (
                <Grid2 container spacing={2} sx={{ mt: 4 }}>
                  <Grid2 item size={{ xs: 12, sm: 6 }}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6">Selected Products</Typography>

                      {/* Display recurring products */}
                      {selectedProducts.filter((p) => p.recurring).length >
                        0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="subtitle1">
                            Recurring Subscription:
                          </Typography>
                          {selectedProducts
                            .filter((p) => p.recurring)
                            .map((product) => (
                              <Box key={product.priceId} sx={{ mt: 1 }}>
                                <Typography variant="body1">
                                  {product.productName} - $
                                  {product.price.toFixed(2)}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Billed monthly on the {moment().format("Do")}
                                </Typography>
                              </Box>
                            ))}
                        </Box>
                      )}

                      {/* Display one-time products */}
                      {selectedProducts.filter((p) => !p.recurring).length >
                        0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="subtitle1">
                            One-Time Purchases:
                          </Typography>
                          {selectedProducts
                            .filter((p) => !p.recurring)
                            .map((product) => (
                              <Box key={product.priceId} sx={{ mt: 1 }}>
                                <Typography variant="body1">
                                  {product.productName} - $
                                  {product.price.toFixed(2)}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  One-time purchase
                                </Typography>
                              </Box>
                            ))}
                        </Box>
                      )}

                      <Typography variant="h5" sx={{ mt: 2 }}>
                        Total: $
                        {selectedProducts
                          .reduce((sum, product) => sum + product.price, 0)
                          .toFixed(2)}
                      </Typography>
                    </Paper>
                  </Grid2>
                  <Grid2 item size={{ xs: 12, sm: 6 }}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Checkout
                      </Typography>
                      <CheckoutForm
                        onSuccess={() => {
                          localStorage.clear();
                          window.location.href = "/subscription";
                        }}
                        onPartialSuccess={handlePartialSuccess}
                        price={selectedProducts[0].priceId} // Use the first product's price for initial setup
                        email={email}
                        userProducts={selectedProducts}
                        savedPaymentMethods={paymentMethods}
                        defaultPaymentMethodId={defaultPaymentMethodId}
                        stripeCustomerId={stripeCustomerId}
                        onPaymentMethodAdded={handlePaymentMethodAdded}
                      />
                    </Paper>
                  </Grid2>
                </Grid2>
              )}
            </>
          )}
        </>
      </Elements>
    </Container>
  );
};

export default Payment;
